/*
 * Copyright (c) 2023 CalAmp Corp. All Rights Reserved
 */
import React from 'react';
import {makeStyles} from '@material-ui/core';
import {AugmentedSmarterTrailerSensorData, SensorTypes} from '@ion/core/src/types/entities';
import {isObjectEmpty} from '@ion/core/src/utils/ObjectUtil';
import {getAssetDetailsContentStyles} from '@ion/dls/src/components/assetdetails/current/assetDetailsContentStyles';
import {TombstoneLoader} from '@ion/dls/src/components/loading/TombstoneLoader';
import {AssetDetailsTileContext} from '@ion/dls/src/components/smarterTrailer/assetDetailsTileContext';
import BrakeChamberWidget from '@ion/dls/src/components/smarterTrailer/BrakeChamberWidget';
import {CargoSenseWidget} from '@ion/dls/src/components/smarterTrailer/CargoSenseWidget';
import {DoorStatusWidget} from '@ion/dls/src/components/smarterTrailer/DoorStatusWidget';
import {
  getBrakeStatus,
  getCargoSensorMessageTime,
  getDoorStatus,
  getLightOutDetectionMessageTime,
  getTemperature,
  getTirePressure,
  getWeightSensorMessageTime
} from '@ion/dls/src/components/smarterTrailer/helper';
import {LightOutDetectionWidget} from '@ion/dls/src/components/smarterTrailer/LightOutDetectionWidget';
import {TemperatureWidget} from '@ion/dls/src/components/smarterTrailer/TemperatureWidget';
import {TirePressureWidget} from '@ion/dls/src/components/smarterTrailer/TirePressureWidget';
import {WeightWidget} from '@ion/dls/src/components/smarterTrailer/WeightWidget';
import {CargoIcon} from '@ion/dls/src/icons/CargoIcon';
import {TemperatureIcon} from '@ion/dls/src/icons/TemperatureIcon';
import {WheelHubIcon} from '@ion/dls/src/icons/WheelHubIcon';
import {themedClassName} from '@ion/dls/src/theme/helpers';
import {dlsColor} from '@ion/dls/src/theme/theme';
import {useThemeContext} from '@ion/dls/src/theme/useThemeContext';
import {DLSTheme} from '@ion/dls/src/types/types';

export interface SensorWidgetContainerProps {
  setSelectedSensor: Function,
  smarterTrailer: AugmentedSmarterTrailerSensorData,
  loading: boolean,
};

const useStyles = makeStyles((theme: DLSTheme) => {
  const contentStyles = getAssetDetailsContentStyles();
  return {
    content : {
      maxWidth                       : 330,
      display                        : 'grid',
      gridTemplateColumns            : 'repeat(4, 1fr)',
      gridTemplateRows               : 'auto auto',
      [theme.breakpoints.down('sm')] : {
        minWidth : '100%'
      }
    },
    detailTombstone : {
      height  : '10vh',
      padding : '10px'
    },
    sensorCard : {
      gridColumn   : 'span 1',
      gridRow      : 'span 1',
      boxSizing    : 'border-box',
      padding      : 8,
      maxWidth     : 80,
      borderRadius : 4,
      transition   : 'background-color 200ms ease-in-out',
      '&:hover'    : {
        cursor          : 'pointer',
        backgroundColor : dlsColor('paleBlue')
      }
    },
    // current theme only - the widget overview lays out as a wrapped row of
    // equal tiles (ION-7522), the same "flex-wrap at a fixed content width"
    // approach as DiagnosticTileGrid, filling the card's 424px content box
    // instead of the legacy 330px 4-column grid ...
    currentContent : {
      maxWidth : 'none',
      width    : '100%',
      display  : 'flex',
      flexWrap : 'wrap',
      gap      : '16px'
    },
    currentSensorCard : {
      ...contentStyles.tile,
      flex      : '1 1 0',
      minWidth  : '192px',
      maxWidth  : 'none',
      boxSizing : 'border-box'
    }
  };
});

/**
 * Defines the SensorWidgetContainer component
 *
 * ```typescript
 *
 * import SensorWidgetContainer from '@ion/features/src/smarterTrailer';
 *
 * <SensorWidgetContainer smarterTrailer={smarterTrailer} loading={loading}/>
 *
 * ```
 */
const SensorWidgetContainer = ({
  setSelectedSensor,
  smarterTrailer,
  loading
}: SensorWidgetContainerProps) => {
  const classes           = useStyles();
  const {isCurrent}       = useThemeContext();
  const doorStatus        = getDoorStatus(smarterTrailer);
  const tirePressure      = getTirePressure(smarterTrailer.TPMS);
  const tireTemperature   = getTemperature(smarterTrailer.TPMS);
  const wheelTemperature  = getTemperature(smarterTrailer.WETS);
  const cargoTimestamp    = getCargoSensorMessageTime(smarterTrailer.CargoSense);
  const bmsStatus         = getBrakeStatus(smarterTrailer.BMS.data);
  const weightTimestamp   = getWeightSensorMessageTime(smarterTrailer.WMS);
  const lightOutTimestamp = getLightOutDetectionMessageTime(smarterTrailer.LMS);

  const {
    CargoSense: cargoSense,
    DoorState: doorState,
    TPMS: tpms,
    WETS: wets,
    BMS : bms,
    WMS: weight,
    LMS: lightOutDetection
  } = smarterTrailer || {};

  const sensorDataExists = (...data) => {
    const hasData = data.filter(item => !isObjectEmpty(item));
    return Boolean(hasData.length);
  };

  const setSelectedSensorById = (id: string) => {
    setSelectedSensor(id as SensorTypes);
  };

  const sensorCardClass = themedClassName(classes.sensorCard, classes.currentSensorCard, isCurrent);

  if (!loading) {
    return (
      <AssetDetailsTileContext.Provider value={true}>
        <div className={themedClassName(classes.content, classes.currentContent, isCurrent)}>
          {sensorDataExists(doorState) && doorStatus && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.DoorState);
              }}>
              <DoorStatusWidget doorStatus={doorStatus} />
            </span>
          )}
          {sensorDataExists(tpms) && tirePressure && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.TPMS);
              }}>
              <TirePressureWidget tirePressure={tirePressure}
                sensorType={SensorTypes.TPMS}/>
            </span>
          )}
          {sensorDataExists(wets) && wheelTemperature && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.WETS);
              }}>
              <TemperatureWidget temperature={wheelTemperature}
                sensorType={SensorTypes.WETS}
                icon={<WheelHubIcon />}/>
            </span>
          )}
          {sensorDataExists(tpms) && tireTemperature && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.TPMS);
              }}>
              <TemperatureWidget temperature={tireTemperature}
                sensorType={SensorTypes.TPMS}
                icon={<TemperatureIcon size={30} />} />
            </span>
          )}
          {sensorDataExists(cargoSense) && cargoTimestamp && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.CargoSense);
              }}>
              <CargoSenseWidget timestamp={cargoTimestamp}
                trailerData={smarterTrailer}
                sensorType={SensorTypes.CargoSense}
                icon={<CargoIcon size={30} />} />
            </span>
          )}
          {sensorDataExists(weight) && weightTimestamp && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.WMS);
              }}>
              <WeightWidget timestamp={weightTimestamp}
                trailerData={smarterTrailer}
                sensorType={SensorTypes.CargoSense} />
            </span>
          )}
          {sensorDataExists(bms) && bmsStatus && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.BMS);
              }}>
              <BrakeChamberWidget trailerData={smarterTrailer}
                status={bmsStatus} />
            </span>
          )}
          {sensorDataExists(lightOutDetection) && lightOutTimestamp && (
            <span className={sensorCardClass}
              onClick={function() {
                setSelectedSensorById(SensorTypes.LMS);
              }}>
              <LightOutDetectionWidget timestamp={lightOutTimestamp}
                trailerData={smarterTrailer}
                sensorType={SensorTypes.LMS}/>
            </span>
          )}
        </div>
      </AssetDetailsTileContext.Provider>
    );
  }
  return (
    <div className={classes.detailTombstone}>
      <TombstoneLoader />
    </div>
  );
};

export default SensorWidgetContainer;
